import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import useAtomWithRef from '../../../../utils/useAtomWithRef'
import {
  fullscreenAtom,
  ownsNFTAtom,
  rControlsVisibilityAtom,
  timelineExcludedVisibilityAtom,
  videoElementAtom,
  wControlsVisibilityAtom,
  wVisibilityTimerIdAtom,
} from '../atoms'
import FullscreenToggle from '../FullscreenToggle'
import PlayPauseButton from '../PlayPauseButton'
import TimeDisplay from '../TimeDisplay'
import MobileTimeline from './MobileTimeline'
import TapRecognizer from './TapRecognizer'
import PreviewText from '../PreviewText'
import LockDisplay from '../LockDisplay'
import { VideoData } from '../../../../interfaces'
import useInterval from '../../../../utils/useInterval'
import { getTimeRemaining } from '../../../../utils/time'

/*======================== React component ===================================*/

const MobileControls = ({
  video,
  scheduledTime,
}: {
  video: VideoData
  scheduledTime: Date
}): JSX.Element => {
  // When the user is scrubbing, the controls should be hidden.
  const [controlsExcludedTimelineVisible] = useAtom(timelineExcludedVisibilityAtom)
  const [fullscreen] = useAtom(fullscreenAtom)
  const [controlsVisibleRef, controlsVisible] = useAtomWithRef(rControlsVisibilityAtom)
  const [, setControlsVisible] = useAtom(wControlsVisibilityAtom)

  const excludedVisible = controlsExcludedTimelineVisible ? controlsVisible : false

  const [, setVisibilityTimerId] = useAtom(wVisibilityTimerIdAtom)
  const [videoElementRef, videoElement] = useAtomWithRef(videoElementAtom)

  const [ownsNFT] = useAtom(ownsNFTAtom)
  const [live, setIsLive] = useState(scheduledTime <= new Date())

  useInterval(() => {
    const time = getTimeRemaining(scheduledTime)
    if (time.days <= 0 && time.hours <= 0 && time.minutes <= 0 && time.seconds <= 0) {
      setIsLive(true)
    } else {
      setIsLive(false)
    }
  }, 1000)

  useEffect(() => {
    setControlsVisible(true)
    setVisibilityTimerId('restart')
  }, [])

  useEffect(() => {
    if (!videoElement) return

    videoElement.addEventListener('play', () => {
      if (controlsVisibleRef.current) {
        // Creates a new timer that will hide the controls after three seconds.
        // The obtained timer id is stored in the visibilityTimerAtom atom.
        setVisibilityTimerId('start')
      }
    })

    videoElement.addEventListener('pause', () => {
      // Cancel the timer, as the controls should not hide automatically when
      // the video is paused.
      setVisibilityTimerId('cancel')
    })
  }, [videoElement])

  // When the visibility of the controls changes, then we need to start a timer
  // which will hide the controls again.
  useEffect(() => {
    // When the controls are hidden manually, cancel any timer which hides them
    // again, as it's useless.
    // Or when the controls get hidden again, then clear the timer id stored in
    // the visibilityTimerAtom atom.
    if (!controlsVisible) {
      setVisibilityTimerId('cancel')
    }

    // When the player isn't paused and the controls are visible, then we need
    // to start a timer which will hide the controls again.
    const isVideoPlaying = videoElementRef.current ? !videoElementRef.current.paused : false
    if (controlsVisible && isVideoPlaying) {
      setVisibilityTimerId('restart')
    }
  }, [controlsVisible])

  return (
    <>
      <Wrapper>
        <TapRecognizer />
        <Visibility isVisible={excludedVisible}>
          <Center>
            <Container width="80px" height="80px">
              <PlayPauseButton />
            </Container>
          </Center>
        </Visibility>
        <BottomControls isFullscreen={fullscreen}>
          <BottomControlBar isFullscreen={fullscreen}>
            <Visibility isVisible={excludedVisible}>
              <TextRow>
                <TimeDisplay />
                {!ownsNFT && (video.gated || !live) && '-'}
                {!ownsNFT && (video.gated || !live) && <PreviewText />}
              </TextRow>
            </Visibility>
            <Visibility isVisible={excludedVisible}>
              <SizedBox height={DEFAULT_ICONS_HEIGHT} width={DEFAULT_ICONS_HEIGHT}>
                <FullscreenToggle />
              </SizedBox>
            </Visibility>
          </BottomControlBar>
          <MobileTimeline isVisible={controlsVisible} />
        </BottomControls>
      </Wrapper>
      {(video.gated || !live) && (
        <LeftBottomCornerPositioned>
          <LockDisplay />
        </LeftBottomCornerPositioned>
      )}
    </>
  )
}

export default MobileControls

const DEFAULT_ICONS_HEIGHT = '25px'

/*======================== Styled components =================================*/

const LeftBottomCornerPositioned = styled.div`
  position: absolute;
  bottom: 15px;
  left: 15px;
`

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  background-color: rgba(0, 0, 0, 0.2);
`

const BottomControlBar = styled.div<{ isFullscreen: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: ${({ isFullscreen }) => (isFullscreen ? '95%' : '85%')};
  margin-bottom: 5px;
`

const Visibility = styled.div<{ isVisible: boolean }>`
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 300ms ease-out, visibility linear 300ms;
`

const BottomControls = styled.div<{ isFullscreen: boolean }>`
  position: absolute;
  bottom: ${({ isFullscreen }) => (isFullscreen ? '30px' : '20px')};
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ isFullscreen }) => (isFullscreen ? '10px' : '12px')};
`

const Center = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const Container = styled.div<{ height: string; width: string }>`
  height: ${(props) => props.height};
  width: ${(props) => props.width};
`

const SizedBox = styled.div<{ height: string; width: string }>`
  height: ${(props) => props.height};
  width: ${(props) => props.width};
`

const TextRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: baseline;
`
